/**
 * The pieces every screen reuses.
 *
 * Deliberately small and unclever: these are styling and semantics, never
 * behaviour. Anything that decides something belongs where it can be tested.
 */

import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';
import { forwardRef, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'danger' | 'quiet';
  busy?: boolean;
};

export function Button({
  variant = 'secondary',
  busy = false,
  className = '',
  children,
  disabled,
  ...rest
}: ButtonProps) {
  const styles = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    danger: 'btn-danger',
    quiet: 'btn text-ink/55 hover:text-ink hover:bg-veil/5',
  }[variant];

  return (
    <button
      {...rest}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={`${styles} ${className}`}
    >
      {busy ? 'Working…' : children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Fields
// ---------------------------------------------------------------------------

interface FieldProps {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
  /** Set when a value is required and a form should say so before submission. */
  required?: boolean;
}

export function Field({ label, hint, error, children, required }: FieldProps) {
  return (
    <label className="block">
      <span className="label">
        {label}
        {required && <span className="ml-1 text-ink/30">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
      {error ? (
        <p className="mt-1.5 text-xs text-danger">{error}</p>
      ) : hint ? (
        <p className="mt-1.5 text-xs leading-relaxed text-ink/40">{hint}</p>
      ) : null}
    </label>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className = '', ...rest }, ref) {
    return <input ref={ref} {...rest} className={`input ${className}`} />;
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className = '', ...rest }, ref) {
    return <textarea ref={ref} {...rest} className={`input ${className}`} />;
  },
);

export function Select({
  className = '',
  children,
  ...rest
}: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...rest} className={`input ${className}`}>
      {children}
    </select>
  );
}

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

export function Card({
  title,
  description,
  actions,
  children,
  className = '',
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <header className="mb-4 flex items-start justify-between gap-4">
          <div>
            {title && <h2 className="text-[15px] font-semibold text-ink">{title}</h2>}
            {description && (
              <p className="mt-1 max-w-prose text-[13px] leading-relaxed text-ink/50">
                {description}
              </p>
            )}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function Chip({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'accent' | 'good' | 'danger' | 'muted' | 'warn';
  children: ReactNode;
}) {
  const styles = {
    neutral: 'chip-neutral',
    accent: 'chip-accent',
    good: 'chip-good',
    danger: 'chip-danger',
    muted: 'chip-muted',
    warn: 'bg-warn/15 text-warn',
  }[tone];
  return <span className={`chip ${styles}`}>{children}</span>;
}

export function Empty({ title, detail }: { title: string; detail?: string }) {
  return (
    <div className="rounded-lg border border-dashed border-veil/10 px-6 py-10 text-center">
      <p className="text-sm text-ink/60">{title}</p>
      {detail && <p className="mx-auto mt-1.5 max-w-prose text-xs text-ink/35">{detail}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

export function Notice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warn' | 'danger' | 'good';
  children: ReactNode;
}) {
  const styles = {
    info: 'border-veil/10 bg-veil/[0.03] text-ink/70',
    warn: 'border-warn/30 bg-warn/[0.07] text-warn',
    danger: 'border-danger/30 bg-danger/[0.07] text-danger',
    good: 'border-good/30 bg-good/[0.07] text-good',
  }[tone];
  return (
    <div role="status" className={`rounded-lg border px-4 py-3 text-[13px] leading-relaxed ${styles}`}>
      {children}
    </div>
  );
}

/**
 * Copies to the clipboard and says so, briefly.
 *
 * Used wherever the value on screen is the only copy that exists — an invite
 * link, an API key shown once. The confirmation matters more than it looks: a
 * silent copy leaves somebody unsure whether the thing they cannot recover is
 * safely somewhere else.
 */
export function CopyButton({
  value,
  label = 'Copy',
  className = '',
}: {
  value: string;
  label?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      className={`btn-secondary px-3 py-1.5 text-xs ${className}`}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
        } catch {
          // A refused clipboard is not worth an error dialog; the value is on
          // screen and can be selected by hand.
        }
      }}
    >
      {copied ? 'Copied' : label}
    </button>
  );
}

/**
 * A destructive action that asks first, in place.
 *
 * Two clicks rather than a modal: the second click is the confirmation, and the
 * label says exactly what it will do.
 */
export function ConfirmButton({
  onConfirm,
  children,
  confirmLabel = 'Really?',
  variant = 'danger',
  busy,
}: {
  onConfirm: () => void;
  children: ReactNode;
  confirmLabel?: string;
  variant?: 'danger' | 'secondary';
  busy?: boolean;
}) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const timer = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(timer);
  }, [armed]);

  return (
    <Button
      variant={armed ? 'danger' : variant}
      busy={busy}
      onClick={() => {
        if (armed) {
          setArmed(false);
          onConfirm();
        } else {
          setArmed(true);
        }
      }}
    >
      {armed ? confirmLabel : children}
    </Button>
  );
}
